import { useState, useEffect, useRef } from 'react';
import LocationSelect from '../components/shared/LocationSelect.jsx';
import { apiFetch, useApi, getToken } from '../hooks/useApi.js';
import PrintButton from '../components/shared/PrintButton.jsx';
import ClientSelect from '../components/shared/ClientSelect.jsx';
import SampleParcelSection from '../components/shared/SampleParcel.jsx';
import { uiAlert, uiConfirm, uiPrompt } from '../utils/dialogs.js';
import { fmtAbv } from '../utils/format.js';
import { strengthFromReading, correctTo } from '../utils/oiml.js';

// ── Shared UI primitives ──────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = {
    borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none',
    fontSize: size === 'sm' ? 12 : 14,
    padding: size === 'sm' ? '5px 12px' : '9px 18px',
    opacity: props.disabled ? 0.5 : 1,
    transition: 'opacity .15s',
  };
  const v = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    ghost:   { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger:  { background: '#dc2626', color: '#fff' },
  };
  return <button {...props} style={{ ...base, ...v[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', ...props.style }} />;
}

function Select({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', ...props.style }}>{children}</select>;
}

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_COLOURS = {
  planned:     { bg: '#eff6ff', text: '#1d4ed8' },
  in_progress: { bg: '#fef9c3', text: '#854d0e' },
  paused:      { bg: '#fff7ed', text: '#c2410c' },
  completed:   { bg: '#dcfce7', text: '#166534' },
  signed_off:  { bg: '#f0fdf4', text: '#15803d' },
  cancelled:   { bg: '#f3f4f6', text: '#6b7280' },
};

function StatusBadge({ status }) {
  const c = STATUS_COLOURS[status] ?? { bg: '#f3f4f6', text: '#6b7280' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: c.bg, color: c.text }}>
      {status?.replace('_', ' ')}
    </span>
  );
}

// Maps a run's business context onto the recipe field that actually carries
// the same distinction — recipes don't have a business_context of their
// own, they have owner_type (PLD / CONTRACT), which is the same split.
const BUSINESS_CONTEXT_TO_OWNER_TYPE = { OWN_BRAND: 'PLD', CONTRACT: 'CONTRACT' };
const RUN_RECIPE_TYPES = [
  { value: 'bulk_liquid', label: 'Bulk liquid' },
  { value: 'packaging',   label: 'Packaging' },
];

// ── Create Run modal ──────────────────────────────────────────────────────────
function CreateRunModal({ onClose, onCreated }) {
  const { data: recipesData } = useApi('/recipes');
  const [form, setForm] = useState({
    business_context: 'OWN_BRAND',
    recipe_id: '',
    scheduled_start_date: '',
    scheduled_end_date: '',
    target_volume_litres: '',
    operates_in_bond: true,
    notes: '',
  });
  const [recipeTypeFilter, setRecipeTypeFilter] = useState('bulk_liquid');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const body = {
        ...form,
        recipe_id: form.recipe_id || undefined,
        target_volume_litres: form.target_volume_litres ? parseFloat(form.target_volume_litres) : undefined,
        scheduled_start_date: form.scheduled_start_date || undefined,
        scheduled_end_date: form.scheduled_end_date || undefined,
      };
      const res = await apiFetch('/production/runs', { method: 'POST', body });
      // A run from a recipe gets the recipe's stages and ingredients (planned —
      // nothing comes off stock until each one is taken).
      if (body.recipe_id) {
        try { await apiFetch(`/production/runs/${res.production_run.id}/populate-from-recipe`, { method: 'POST' }); }
        catch (err) { await uiAlert(`The run was made, but its stages couldn't be set up from the recipe: ${err.message}`); }
      }
      onCreated(res.production_run);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const activeRecipes = (recipesData?.recipes ?? recipesData ?? []).filter(r => r.status === 'active');
  const filteredRecipes = activeRecipes.filter(r =>
    r.recipe_type === recipeTypeFilter &&
    r.owner_type === BUSINESS_CONTEXT_TO_OWNER_TYPE[form.business_context]
  );

  // Business context or recipe type changing can invalidate whatever recipe
  // was already picked — drop it rather than silently submitting a run with
  // a recipe that doesn't match either filter.
  function handleBusinessContextChange(value) {
    set('business_context', value);
    set('recipe_id', '');
  }
  function handleRecipeTypeFilterChange(value) {
    setRecipeTypeFilter(value);
    set('recipe_id', '');
  }

  return (
    <Modal title="New Production Run" onClose={onClose}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Business context">
          <Select value={form.business_context} onChange={e => handleBusinessContextChange(e.target.value)}>
            <option value="OWN_BRAND">Own Brand (PL-B-…)</option>
            <option value="CONTRACT">Contract (PL-C-…)</option>
          </Select>
        </Field>

        <Field label="Recipe type">
          <Select value={recipeTypeFilter} onChange={e => handleRecipeTypeFilterChange(e.target.value)}>
            {RUN_RECIPE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </Field>

        <Field label="Recipe (optional)">
          <Select value={form.recipe_id} onChange={e => set('recipe_id', e.target.value)}>
            <option value="">— No recipe —</option>
            {filteredRecipes.map(r => (
              <option key={r.id} value={r.id}>{r.name} ({r.spirit_type})</option>
            ))}
          </Select>
          {filteredRecipes.length === 0 && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
              No active {RUN_RECIPE_TYPES.find(t => t.value === recipeTypeFilter)?.label.toLowerCase()} recipes for {form.business_context === 'CONTRACT' ? 'Contract' : 'Own Brand'} yet.
            </p>
          )}
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Scheduled start">
            <Input type="date" value={form.scheduled_start_date} onChange={e => set('scheduled_start_date', e.target.value)} />
          </Field>
          <Field label="Scheduled end">
            <Input type="date" value={form.scheduled_end_date} onChange={e => set('scheduled_end_date', e.target.value)} />
          </Field>
        </div>

        <Field label="Target volume (litres)">
          <Input type="number" min="0" step="0.01" value={form.target_volume_litres} onChange={e => set('target_volume_litres', e.target.value)} placeholder="e.g. 1000" />
        </Field>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.operates_in_bond} onChange={e => set('operates_in_bond', e.target.checked)} />
          Operates in bond (HMRC W1 reporting applies)
        </label>

        <Field label="Notes">
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            rows={3} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
        </Field>

        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create Run'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Section header helper ─────────────────────────────────────────────────────
function SectionHeader({ title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, marginTop: 28 }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
      {action}
    </div>
  );
}

// ── Stock item picker (typeahead) ─────────────────────────────────────────────
function StockItemPicker({ label, items, value, search, onSearchChange, onSelect }) {
  const [open, setOpen] = useState(false);
  const filtered = (items ?? []).filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.sku ?? '').toLowerCase().includes(search.toLowerCase())
  ).slice(0, 12);

  return (
    <Field label={label}>
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <Input
            value={search}
            onChange={e => { onSearchChange(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Search by name or SKU…"
            style={{ flex: 1 }}
          />
          {value && (
            <button type="button" onClick={() => { onSelect(null); onSearchChange(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: '0 6px' }}>×</button>
          )}
        </div>
        {open && filtered.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 400, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
            {filtered.map(item => (
              <div key={item.id}
                onMouseDown={() => { onSelect(item); setOpen(false); }}
                style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.sku}{item.category ? ` · ${item.category}` : ''}</div>
                </div>
                {item.abv != null && <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>{fmtAbv(item.abv)}%</span>}
                {item.is_alcoholic && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--pld-green)', background: '#f0fdf4', borderRadius: 4, padding: '2px 6px' }}>ALC</span>}
              </div>
            ))}
          </div>
        )}
        {open && search && filtered.length === 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 400, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
            No items found
          </div>
        )}
        {value && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pld-green)', fontWeight: 600 }}>✓ Item selected</p>}
      </div>
    </Field>
  );
}

// ── Run detail view ───────────────────────────────────────────────────────────
// Bottle labels for a sample from this run: the 100×150 label printer,
// two labels a sheet with a cut line halfway down — same as recipe
// development samples, headed PRODUCTION SAMPLE and carrying the batch.
function ProductionSampleLabelModal({ run, outputs, onClose }) {
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData?.data ?? (Array.isArray(clientData) ? clientData : []);
  const main = outputs.find(o => o.output_type === 'primary' && o.item_name) || outputs.find(o => o.item_name);
  const [l, setL] = useState({
    product: run.recipe_name || main?.item_name || '',
    client: '',
    batch: run.run_ref || '',
    abv: main?.abv != null ? String(Number(main.abv)) : '',
    size: '',
    date: new Date().toLocaleDateString('en-GB'),
    note: '',
  });
  const clientName = clients.find(c => c.id === run.client_id)?.name;
  useEffect(() => { if (clientName) setL(x => (x.client ? x : { ...x, client: clientName })); }, [clientName]);
  const set = k => e => setL(x => ({ ...x, [k]: e.target.value }));
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const parcelRef = useRef(null);
  const [sending, setSending] = useState(false);
  async function preview() {
    setBusy(true); setErr('');
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`/api/production/runs/${run.id}/sample-label.pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ top: l }) });
      if (!res.ok) throw new Error(`Couldn't make the label (HTTP ${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <Modal title="Sample bottle labels" onClose={onClose} width={600}>
      {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>Prints on the 100×150 label printer as two labels with a cut line halfway down — cut it in half for two bottles. Check the details: this goes to the customer.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Product name"><Input value={l.product} onChange={set('product')} /></Field>
        <Field label="Client"><ClientSelect value={l.client} onChange={(v) => setL(x => ({ ...x, client: v }))} /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr .7fr .7fr 1fr', gap: 12 }}>
        <Field label="Batch"><Input value={l.batch} onChange={set('batch')} /></Field>
        <Field label="ABV %"><Input value={l.abv} onChange={set('abv')} /></Field>
        <Field label="Size"><Input value={l.size} onChange={set('size')} placeholder="e.g. 5cl" /></Field>
        <Field label="Date"><Input value={l.date} onChange={set('date')} /></Field>
      </div>
      <Field label="Footer"><Input value={l.note} onChange={set('note')} placeholder="Not for sale · for evaluation only" /></Field>
      <SampleParcelSection ref={parcelRef} clientName={l.client} kind="production" onSendChange={setSending} runId={run.id}
        reference={run.run_ref} description={`Samples — ${l.product || 'production'}`.slice(0, 35)} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="ghost" onClick={preview} disabled={busy}>{busy ? 'Making…' : 'Preview'}</Btn>
        <PrintButton docType="production_sample_label" method="POST" path={`/production/runs/${run.id}/sample-label.pdf`} getBody={() => ({ top: l })} title={`Sample labels — ${l.product} ${l.batch}`.trim()} size="md"
          label={sending ? 'Print & send' : 'Print'} onDone={() => parcelRef.current?.queue()} />
      </div>
    </Modal>
  );
}

// Case labels for the finished product a run made (Joe, 29 Oct 2026): opens
// straight onto the labels, filled in from the run (product, bottles per
// case, size, ABV, batch, dates — see GET /production/runs/:id/case-labels).
// "Change details" shows the fields if anything needs adjusting.
// What one case label looks like (both halves the same), drawn on the page.
function CaseLabelPreview({ l }) {
  const size = String(l.size || '').trim();
  let product = String(l.product || 'Product').trim();
  if (size && product.toLowerCase().endsWith(size.toLowerCase())) product = product.slice(0, -size.length).trim() || product;
  const contents = !l.per_case ? size : size ? `${l.per_case} × ${size}` : `${l.per_case} bottles`;
  const abv = l.abv ? `${String(l.abv).replace(/%$/, '')}% ABV` : '';
  const half = (
    <div style={{ height: '50%', boxSizing: 'border-box', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' }}>
      {l.client && <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase' }}>{l.client}</div>}
      <div style={{ fontSize: product.length > 22 ? 15 : 20, fontWeight: 800, lineHeight: 1.05 }}>{product}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 800 }}><span>{contents}</span><span>{abv}</span></div>
      <div style={{ borderTop: '2px solid #000', margin: '2px 0' }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <div aria-hidden="true" style={{ width: 58, height: 58, flex: '0 0 auto', background: 'repeating-linear-gradient(45deg,#000 0 3px,#fff 3px 6px)', border: '3px solid #000' }} />
        <div style={{ minWidth: 0, fontWeight: 800 }}>
          {l.bottle_batch
            ? <><div style={{ fontSize: 6 }}>BATCH NO.</div><div style={{ fontSize: 20, lineHeight: 1 }}>{l.bottle_batch}</div><div style={{ fontSize: 6, marginTop: 2 }}>{/^lot/i.test(String(l.batch)) ? l.batch : `LOT ${l.batch}`}</div></>
            : <><div style={{ fontSize: 6 }}>BATCH</div><div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{l.batch}</div></>}
          {l.bottled && <><div style={{ fontSize: 6, marginTop: 3 }}>BOTTLED</div><div style={{ fontSize: 10 }}>{l.bottled}</div></>}
          {l.best_before && <><div style={{ fontSize: 6, marginTop: 3 }}>BEST BEFORE</div><div style={{ fontSize: 10 }}>{l.best_before}</div></>}
        </div>
      </div>
    </div>
  );
  return (
    <div style={{ width: 240, height: 360, background: '#fff', color: '#000', border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'Helvetica, Arial, sans-serif', boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
      {half}
      <div style={{ borderTop: '1px dashed #000' }} />
      {half}
    </div>
  );
}

function CaseLabelModal({ run, onClose }) {
  const { data } = useApi(`/production/runs/${run.id}/case-labels`, [run.id]);
  const options = Array.isArray(data) ? data : (data?.data ?? []);
  const [pick, setPick] = useState(null);
  const [l, setL] = useState(null);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState('');
  const choose = (o) => {
    setPick(o?.output_id || null);
    setL({ product: o?.product || run.recipe_name || '', client: o?.client || '', size: o?.size || '', abv: o?.abv || '',
      per_case: o?.per_case || '6', batch: o?.batch || run.run_ref, bottle_batch: o?.bottle_batch || '', bottled: o?.bottled || new Date().toLocaleDateString('en-GB'),
      best_before: o?.best_before || '', cases: o?.cases || '1', units: o?.units || 0 });
  };
  useEffect(() => { if (data && !l) choose(options[0]); }, [data]);
  async function openPdf() {
    setErr('');
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`/api/production/runs/${run.id}/case-labels.pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify(l) });
      if (!res.ok) throw new Error(`Couldn't make the labels (HTTP ${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); setErr(e.message); }
  }
  const set = k => e => setL(x => {
    const n = { ...x, [k]: e.target.value };
    if (k === 'per_case' && x.units && Number(e.target.value) > 0) n.cases = String(Math.max(1, Math.ceil(x.units / Number(e.target.value))));
    return n;
  });
  return (
    <Modal title={l ? `Case labels — ${l.product}` : 'Case labels'} onClose={onClose} width={720}>
      {!l ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p> : (<>
        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <CaseLabelPreview l={l} />
          <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
            <div><strong>{l.cases}</strong> label{Number(l.cases) === 1 ? '' : 's'} — one per case of {l.per_case}{l.units ? ` (${l.units} made)` : ''}.</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Stick each one over the top edge of the box: half on top, half on the side.</div>
            {options.length > 1 && (
              <Field label="For">
                <select value={pick || ''} onChange={(e) => choose(options.find((o) => o.output_id === e.target.value))} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%' }}>
                  {options.map((o) => <option key={o.output_id} value={o.output_id}>{o.product} — {o.units || ''} {o.unit} · {o.batch}</option>)}
                </select>
              </Field>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <PrintButton docType="case_label" method="POST" path={`/production/runs/${run.id}/case-labels.pdf`} getBody={() => ({ ...l })} title={`Case labels — ${l.product} ${l.batch}`.trim()} size="md" label={`Print ${l.cases}`} />
              <Btn variant="ghost" onClick={openPdf}>Open PDF</Btn>
              <Btn variant="ghost" onClick={() => setEditing((v) => !v)}>{editing ? 'Hide details' : 'Change details'}</Btn>
            </div>
            {editing && (<>
              <Field label="Product"><Input value={l.product} onChange={set('product')} /></Field>
              <Field label="Client / brand (optional)"><Input value={l.client} onChange={set('client')} /></Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                <Field label="Bottles per case"><Input type="number" min="1" value={l.per_case} onChange={set('per_case')} /></Field>
                <Field label="Number of labels"><Input type="number" min="1" max="500" value={l.cases} onChange={set('cases')} /></Field>
                <Field label="Bottle size"><Input value={l.size} onChange={set('size')} placeholder="e.g. 70cl" /></Field>
                <Field label="ABV %"><Input value={l.abv} onChange={set('abv')} /></Field>
                <Field label="Batch"><Input value={l.batch} onChange={set('batch')} /></Field>
                <Field label="Bottled"><Input value={l.bottled} onChange={set('bottled')} /></Field>
              </div>
              <Field label="Best before"><Input value={l.best_before} onChange={set('best_before')} placeholder="leave blank if none" /></Field>
            </>)}
          </div>
        </div>
      </>)}
    </Modal>
  );
}

// ── Duty (W1), QC and sign-off — carried over from the old run screen ────────
function W1Panel({ run }) {
  const { data: w1Cls } = useApi(`/production/runs/${run.id}/w1-classification`);
  return (
        <div>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>W1 Classification Preview</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
              Shows how this run's outputs will be classified on the HMRC W1 return.
            </p>
          </div>

          {!run.operates_in_bond ? (
            <div style={{ padding: '14px 18px', background: '#f8fafc', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', fontSize: 14, color: 'var(--text-muted)' }}>
              W1 classification is not applicable — this run does not operate in bond.
            </div>
          ) : !w1Cls ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading classification…</p>
          ) : (
            <>
              {/* Input summary */}
              <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '14px 18px', marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--text)' }}>Input composition</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                  {[
                    { label: 'Run type',          value: (w1Cls.input_summary?.run_type ?? '—').replace('_', ' ') },
                    { label: 'Alcoholic inputs',  value: `${Number(w1Cls.input_summary?.total_alcoholic_lpa ?? 0).toFixed(3)} LPA` },
                    { label: 'Non-alc. inputs',   value: `${Number(w1Cls.input_summary?.total_non_alcoholic_lpa ?? 0).toFixed(3)} LPA` },
                    { label: 'Non-alc. fraction', value: `${(Number(w1Cls.input_summary?.non_alcoholic_fraction ?? 0) * 100).toFixed(2)}%` },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', textTransform: 'capitalize' }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Q27 / Q34 totals */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Q27 — Spirit created (LPA)', value: w1Cls.q27_lpa, color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
                  { label: 'Q34 — Production losses (LPA)', value: w1Cls.q34_lpa, color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' },
                ].map(({ label, value, color, bg, border }) => (
                  <div key={label} style={{ borderRadius: 'var(--radius)', border: `1px solid ${border}`, background: bg, padding: '16px 20px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 700, color }}>{Number(value ?? 0).toFixed(3)}</div>
                  </div>
                ))}
              </div>

              {/* Per-output classifications */}
              {(w1Cls.classifications ?? []).length > 0 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>Output classifications</div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                          {['Output type', 'LPA', 'W1 type', 'W1 field', 'LPA from non-alc'].map(h => (
                            <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(w1Cls.classifications ?? []).map((cls, i) => (
                          <tr key={cls.output_id} style={{ borderBottom: i < w1Cls.classifications.length - 1 ? '1px solid var(--border)' : 'none' }}>
                            <td style={{ padding: '10px 14px', textTransform: 'capitalize', fontWeight: 600, color: 'var(--text)' }}>{cls.output_type}</td>
                            <td style={{ padding: '10px 14px', fontFamily: 'monospace' }}>{Number(cls.lpa ?? 0).toFixed(3)}</td>
                            <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{cls.w1_movement_type ?? '—'}</td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{
                                display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                                background: cls.w1_field === 'Q27' || cls.w1_field?.startsWith('Q27') ? '#eff6ff' : cls.w1_field === 'Q34' ? '#fff7ed' : '#f3f4f6',
                                color: cls.w1_field === 'Q27' || cls.w1_field?.startsWith('Q27') ? '#1d4ed8' : cls.w1_field === 'Q34' ? '#c2410c' : '#6b7280',
                              }}>{cls.w1_field ?? '—'}</span>
                            </td>
                            <td style={{ padding: '10px 14px', fontFamily: 'monospace' }}>{Number(cls.lpa_from_non_alcoholic ?? 0).toFixed(3)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {(w1Cls.classifications ?? []).length === 0 && (
                <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No outputs logged yet — add outputs to see W1 classification.</p>
              )}
            </>
          )}
        </div>
  );
}

// QC records, with the strength check (21 Oct 2026): a hydrometer reading or
// density at a temperature (+ sugar) → true ABV at 20 °C (OIML R22), checked
// against the stage's or product's target, with the water or spirit to add
// when it's out. The server works the ABV out again and keeps the reading.
const BLANK_QC = {
  check_type: 'end_of_run', stage_id: '', abv: '', volume_litres: '',
  abv_method: 'hydrometer', reading_abv: '', density_measured: '', reading_temp_c: '20', sugar_g_l: '',
  target_abv: '', abv_tolerance: '', spirit_abv: '96.4',
  colour: '', clarity: '', nose: '', palate: '', finish: '',
  result: 'pass', notes: '',
};
const fx = (v, d = 2) => Number(v).toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });

function strengthOf(f) {
  const sugar = Number(f.sugar_g_l) || 0;
  let abv = NaN;
  if (f.abv_method === 'direct') abv = f.abv === '' ? NaN : Number(f.abv);
  else if (f.abv_method === 'hydrometer' && f.reading_abv !== '') abv = strengthFromReading({ method: 'hydrometer', reading: Number(f.reading_abv), temp: Number(f.reading_temp_c || 20), sugar }).abv;
  else if (f.abv_method === 'density' && f.density_measured !== '') abv = strengthFromReading({ method: 'density', density: Number(f.density_measured), temp: Number(f.reading_temp_c || 20), sugar }).abv;
  if (!Number.isFinite(abv) || abv < 0 || abv > 100) return null;
  const target = f.target_abv === '' ? null : Number(f.target_abv);
  const tol = f.abv_tolerance === '' ? 0.3 : Number(f.abv_tolerance);
  const diff = target != null ? abv - target : null;
  const pass = diff == null ? null : Math.abs(diff) <= tol + 1e-9;
  let correction = null;
  const vol = Number(f.volume_litres);
  if (pass === false && vol > 0) {
    const water = abv > target;
    const c = correctTo({ volume: vol, abvNow: abv, target, addAbv: water ? 0 : Number(f.spirit_abv || 96.4), sugar });
    if (c) correction = water
      ? `Add ${fx(c.add)} L water${c.addKg ? ` (${fx(c.addKg)} kg)` : ''} → ${fx(c.final)} L at ${fx(target)}%`
      : `Add ${fx(c.add)} L spirit at ${fx(Number(f.spirit_abv || 96.4), 1)}%${c.addKg ? ` (${fx(c.addKg)} kg)` : ''} → ${fx(c.final)} L at ${fx(target)}%`;
  }
  return { abv, target, tol, diff, pass, correction };
}

function QcPanel({ runId, view, onChanged }) {
  const { data: qcData, refetch: refetchQc } = useApi(`/qc-records/run/${runId}`);
  const qcRecords = qcData?.qc_records ?? (Array.isArray(qcData) ? qcData : []);
  const [showAddQc, setShowAddQc] = useState(false);
  const [qcSaving, setQcSaving] = useState(false);
  const [qcForm, setQcForm] = useState(BLANK_QC);
  const [resultTouched, setResultTouched] = useState(false);
  const stages = view?.stages || [];
  const spec = view?.spec || null;
  const stageById = Object.fromEntries(stages.map((s) => [s.id, s]));

  // What a stage should come out at: its recipe stage's target, or the product spec's for the last stage.
  const targetFor = (sid) => {
    const st = stageById[sid];
    if (st?.target_abv != null) return { abv: st.target_abv, tol: spec?.abv_tolerance ?? null, from: `${st.name} target` };
    if ((!st || st.is_last) && spec?.abv_target != null) return { abv: spec.abv_target, tol: spec.abv_tolerance, from: `${spec.product_name} spec` };
    return null;
  };
  function openAdd() {
    const sid = view?.active_stage_id || stages[stages.length - 1]?.id || '';
    const tg = targetFor(sid);
    setQcForm({ ...BLANK_QC, stage_id: sid, target_abv: tg ? String(tg.abv) : '', abv_tolerance: tg?.tol != null ? String(tg.tol) : '' });
    setResultTouched(false);
    setShowAddQc(true);
  }
  const set = (k) => (e) => setQcForm((f) => {
    const next = { ...f, [k]: e.target.value };
    if (k === 'stage_id') { const tg = targetFor(e.target.value); next.target_abv = tg ? String(tg.abv) : ''; next.abv_tolerance = tg?.tol != null ? String(tg.tol) : ''; }
    return next;
  });
  const calc = strengthOf(qcForm);
  // The result follows the strength check until it's chosen by hand.
  useEffect(() => {
    if (!resultTouched && calc?.pass != null) setQcForm((f) => (f.result === (calc.pass ? 'pass' : 'fail') ? f : { ...f, result: calc.pass ? 'pass' : 'fail' }));
  }, [calc?.pass, resultTouched]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleAddQc(e) {
    e.preventDefault(); setQcSaving(true);
    try {
      const f = qcForm;
      const body = {
        check_type: f.check_type, stage_id: f.stage_id || undefined,
        abv: f.abv_method === 'direct' ? (f.abv !== '' ? parseFloat(f.abv) : undefined) : undefined,
        volume_litres: f.volume_litres ? parseFloat(f.volume_litres) : undefined,
        abv_method: calc || f.abv_method !== 'direct' ? f.abv_method : undefined,
        reading_abv: f.abv_method === 'hydrometer' && f.reading_abv !== '' ? parseFloat(f.reading_abv) : undefined,
        density_measured: f.abv_method === 'density' && f.density_measured !== '' ? parseFloat(f.density_measured) : undefined,
        reading_temp_c: f.abv_method !== 'direct' ? parseFloat(f.reading_temp_c || '20') : undefined,
        sugar_g_l: f.sugar_g_l !== '' ? parseFloat(f.sugar_g_l) : undefined,
        target_abv: f.target_abv !== '' ? parseFloat(f.target_abv) : undefined,
        abv_tolerance: f.target_abv !== '' ? (f.abv_tolerance !== '' ? parseFloat(f.abv_tolerance) : 0.3) : undefined,
        correction: calc?.correction || undefined,
        colour: f.colour || undefined, clarity: f.clarity || undefined, nose: f.nose || undefined,
        palate: f.palate || undefined, finish: f.finish || undefined,
        result: f.result, notes: f.notes || undefined,
      };
      await apiFetch(`/qc-records/run/${runId}`, { method: 'POST', body });
      setShowAddQc(false);
      refetchQc();
    } catch (err) { uiAlert(err.message); }
    finally { setQcSaving(false); }
  }

  // Put a checked strength onto the stage's output (its LPA and stock follow).
  async function applyToOutput(qc, out) {
    if (!(await uiConfirm(`Set ${out.name} to ${fmtAbv(qc.abv)}% ABV (was ${out.abv != null ? `${fmtAbv(out.abv)}%` : 'blank'})? Its pure alcohol and stock are recalculated.`))) return;
    try {
      await apiFetch(`/production/runs/${runId}/flow/outputs/${out.id}`, { method: 'PATCH', body: { abv: Number(qc.abv) } });
      onChanged?.();
    } catch (err) { uiAlert(err.message); }
  }

  const inp = { padding: '6px 8px', fontSize: 13 };
  return (
    <>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Quality Control</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>Strength checks, sensory notes and QC results for this run.</p>
            </div>
            <Btn size="sm" onClick={openAdd}>+ Add QC Record</Btn>
          </div>

          {qcRecords.length === 0 ? (
            <div style={{ padding: '20px 24px', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', fontSize: 14, color: 'var(--text-muted)', textAlign: 'center' }}>
              No QC records yet. Add the first check above.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {qcRecords.map(qc => {
                const resultColour = qc.result === 'pass' ? { bg: '#dcfce7', text: '#166534' } : qc.result === 'fail' ? { bg: '#fee2e2', text: '#991b1b' } : { bg: '#fef9c3', text: '#854d0e' };
                const st = qc.stage_id ? stageById[qc.stage_id] : null;
                const diff = qc.abv != null && qc.target_abv != null ? Number(qc.abv) - Number(qc.target_abv) : null;
                const inTol = diff != null ? Math.abs(diff) <= Number(qc.abv_tolerance ?? 0.3) + 1e-9 : null;
                const outs = (st?.outputs || []).filter((o) => o.kind !== 'waste' && (o.alcoholic || o.abv != null));
                const how = qc.abv_method === 'hydrometer' ? `hydrometer ${fmtAbv(qc.reading_abv)}% at ${Number(qc.reading_temp_c)} °C`
                  : qc.abv_method === 'density' ? `density ${Number(qc.density_measured)} kg/m³ at ${Number(qc.reading_temp_c)} °C` : null;
                return (
                  <div key={qc.id} style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', textTransform: 'capitalize' }}>{qc.check_type?.replace(/_/g, ' ')}</span>
                      {st && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{st.name}</span>}
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: resultColour.bg, color: resultColour.text }}>{qc.result}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{qc.recorded_by_name ? `${qc.recorded_by_name} · ` : ''}{qc.created_at ? new Date(qc.created_at).toLocaleString('en-GB') : ''}</span>
                    </div>
                    {qc.abv != null && (how || qc.target_abv != null) && (
                      <div style={{ fontSize: 13, marginBottom: 10 }}>
                        <strong>{fmtAbv(qc.abv)}% ABV</strong> true at 20 °C
                        {how && <span style={{ color: 'var(--text-muted)' }}> · from {how}{Number(qc.sugar_g_l) > 0 ? `, ${Number(qc.sugar_g_l)} g/L sugar` : ''}</span>}
                        {qc.target_abv != null && (
                          <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: inTol ? '#dcfce7' : '#fee2e2', color: inTol ? '#166534' : '#991b1b' }}>
                            {inTol ? 'ON TARGET' : 'OUT'} · target {fmtAbv(qc.target_abv)} ±{Number(qc.abv_tolerance ?? 0.3)} ({diff >= 0 ? '+' : '−'}{Math.abs(diff).toFixed(2)})
                          </span>
                        )}
                        {qc.correction && <div style={{ marginTop: 4, color: '#9a3412' }}>Correction: {qc.correction}</div>}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginBottom: qc.notes ? 10 : 0 }}>
                      {qc.abv           != null && !how && qc.target_abv == null && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>ABV </span>{fmtAbv(qc.abv)}%</div>}
                      {qc.volume_litres != null && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Vol </span>{qc.volume_litres}L</div>}
                      {qc.lpa           != null && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>LPA </span>{Number(qc.lpa).toFixed(3)}</div>}
                      {qc.density_at_20c != null && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Density 20 °C </span>{Number(qc.density_at_20c)}</div>}
                      {qc.colour  && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Colour </span>{qc.colour}</div>}
                      {qc.clarity && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Clarity </span>{qc.clarity}</div>}
                    </div>
                    {(qc.nose || qc.palate || qc.finish) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: qc.notes ? 10 : 0 }}>
                        {qc.nose   && <div style={{ fontSize: 13 }}><strong style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>Nose</strong> — {qc.nose}</div>}
                        {qc.palate && <div style={{ fontSize: 13 }}><strong style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>Palate</strong> — {qc.palate}</div>}
                        {qc.finish && <div style={{ fontSize: 13 }}><strong style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>Finish</strong> — {qc.finish}</div>}
                      </div>
                    )}
                    {qc.notes && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>{qc.notes}</p>}
                    {qc.abv != null && view?.run?.editable && outs.length > 0 && st?.state === 'done' && outs.some((o) => o.abv == null || Math.abs(o.abv - Number(qc.abv)) >= 0.0005) && (
                      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>{st.name} is finished — reopen it on the Stages tab to use {fmtAbv(qc.abv)}% for its output.</div>
                    )}
                    {qc.abv != null && view?.run?.editable && outs.length > 0 && st?.state !== 'done' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                        {outs.map((o) => (
                          <Btn key={o.id} size="sm" variant="ghost" disabled={o.abv != null && Math.abs(o.abv - Number(qc.abv)) < 0.0005} onClick={() => applyToOutput(qc, o)}>
                            {o.abv != null && Math.abs(o.abv - Number(qc.abv)) < 0.0005 ? `✓ ${o.name} is ${fmtAbv(qc.abv)}%` : `Use ${fmtAbv(qc.abv)}% for ${o.name}`}
                          </Btn>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
{showAddQc && (
        <Modal title="Add QC Record" onClose={() => setShowAddQc(false)} width={600}>
          <form onSubmit={handleAddQc} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Check type">
                <Select value={qcForm.check_type} onChange={set('check_type')}>
                  <option value="end_of_run">End of run</option>
                  <option value="first_fill">First fill</option>
                  <option value="mid_run">Mid run</option>
                  <option value="final">Final</option>
                  <option value="label_check">Label check</option>
                  <option value="other">Other</option>
                </Select>
              </Field>
              <Field label="Stage">
                <Select value={qcForm.stage_id} onChange={set('stage_id')}>
                  <option value="">Whole run</option>
                  {stages.map((s) => <option key={s.id} value={s.id}>{s.n}. {s.name}</option>)}
                </Select>
              </Field>
            </div>

            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 12, display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 13, marginRight: 6 }}>Strength</strong>
                {[['hydrometer', 'Hydrometer'], ['density', 'Density meter'], ['direct', 'ABV (already at 20 °C)']].map(([m, l]) => (
                  <button key={m} type="button" onClick={() => setQcForm((f) => ({ ...f, abv_method: m }))}
                    style={{ padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', background: qcForm.abv_method === m ? 'var(--pld-green)' : 'var(--surface)', color: qcForm.abv_method === m ? '#fff' : 'var(--text)' }}>{l}</button>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {qcForm.abv_method === 'hydrometer' && <Field label="Reading % vol"><Input style={inp} type="number" step="any" value={qcForm.reading_abv} onChange={set('reading_abv')} placeholder="e.g. 40.6" /></Field>}
                {qcForm.abv_method === 'density' && <Field label="Density kg/m³"><Input style={inp} type="number" step="any" value={qcForm.density_measured} onChange={set('density_measured')} placeholder="e.g. 948.2" /></Field>}
                {qcForm.abv_method === 'direct' && <Field label="ABV % at 20 °C"><Input style={inp} type="number" min="0" max="100" step="any" value={qcForm.abv} onChange={set('abv')} placeholder="e.g. 40.0" /></Field>}
                {qcForm.abv_method !== 'direct' && <Field label="Temperature °C"><Input style={inp} type="number" step="any" value={qcForm.reading_temp_c} onChange={set('reading_temp_c')} /></Field>}
                <Field label="Sugar g/L"><Input style={inp} type="number" min="0" step="any" value={qcForm.sugar_g_l} onChange={set('sugar_g_l')} placeholder="0" /></Field>
                <Field label="Target ABV %"><Input style={inp} type="number" step="any" value={qcForm.target_abv} onChange={set('target_abv')} placeholder="none" /></Field>
                <Field label="Tolerance ±"><Input style={inp} type="number" step="any" value={qcForm.abv_tolerance} onChange={set('abv_tolerance')} placeholder="0.3" /></Field>
                <Field label="Volume (litres)"><Input style={inp} type="number" min="0" step="any" value={qcForm.volume_litres} onChange={set('volume_litres')} placeholder="for LPA / correction" /></Field>
              </div>
              {calc && (
                <div style={{ fontSize: 13, background: calc.pass === false ? '#fff7ed' : 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '8px 10px' }}>
                  <strong>{fx(calc.abv)}% ABV</strong> true at 20 °C
                  {qcForm.volume_litres && <span style={{ color: 'var(--text-muted)' }}> · {fx(Number(qcForm.volume_litres) * calc.abv / 100, 3)} LPA</span>}
                  {calc.pass != null && <span style={{ marginLeft: 8, fontWeight: 700, color: calc.pass ? '#166534' : '#991b1b' }}>{calc.pass ? '✓ on target' : `✗ ${calc.diff > 0 ? 'over' : 'under'} by ${Math.abs(calc.diff).toFixed(2)}`}</span>}
                  {calc.pass === false && !calc.correction && <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Enter the volume to see the correction.</div>}
                  {calc.correction && <div style={{ marginTop: 4, color: '#9a3412' }}>{calc.correction}</div>}
                  {calc.pass === false && calc.diff < 0 && (
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}>
                      Fortify with spirit at <Input style={{ ...inp, width: 70 }} type="number" step="any" value={qcForm.spirit_abv} onChange={set('spirit_abv')} /> %
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Colour"><Input value={qcForm.colour} onChange={set('colour')} placeholder="e.g. pale gold" /></Field>
              <Field label="Clarity"><Input value={qcForm.clarity} onChange={set('clarity')} placeholder="e.g. bright" /></Field>
            </div>
            <Field label="Nose"><Input value={qcForm.nose} onChange={set('nose')} placeholder="Tasting notes — nose" /></Field>
            <Field label="Palate"><Input value={qcForm.palate} onChange={set('palate')} placeholder="Tasting notes — palate" /></Field>
            <Field label="Finish"><Input value={qcForm.finish} onChange={set('finish')} placeholder="Tasting notes — finish" /></Field>
            <Field label="Result">
              <Select value={qcForm.result} onChange={(e) => { setResultTouched(true); setQcForm((f) => ({ ...f, result: e.target.value })); }}>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
                <option value="conditional">Conditional</option>
              </Select>
            </Field>
            <Field label="Notes"><Input value={qcForm.notes} onChange={set('notes')} placeholder="Any additional observations" /></Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn type="button" variant="ghost" onClick={() => setShowAddQc(false)}>Cancel</Btn>
              <Btn type="submit" disabled={qcSaving}>{qcSaving ? 'Saving…' : 'Save QC Record'}</Btn>
            </div>
          </form>
        </Modal>
)}
    </>
  );
}

function SignOffPanel({ run, onChanged }) {
  const runId = run.id;
  const { data: signOffStatusData, refetch: refetchSignOffStatus } = useApi(`/production/runs/${runId}/sign-off-status`);
  const { data: mbData } = useApi(`/production/runs/${runId}/mass-balance`);
  const { data: meData } = useApi('/settings/me');
  const isOwner = !!(meData?.data ?? meData)?.is_system_owner;
  const signOffApproval = signOffStatusData?.approval ?? null;
  const massBalance = mbData?.mass_balance ?? null;
  const [signOffSaving, setSignOffSaving] = useState(false);
  const [signOffMsg, setSignOffMsg] = useState('');
  async function handleRequestSignOff() {
    setSignOffSaving(true); setSignOffMsg('');
    try {
      await apiFetch(`/production/runs/${runId}/sign-off-request`, { method: 'POST' });
      refetchSignOffStatus();
      setSignOffMsg('Sign-off requested. A second person must now approve.');
    } catch (err) { setSignOffMsg(`Error: ${err.message}`); }
    finally { setSignOffSaving(false); }
  }
  async function handleApproveSignOff(approvalId) {
    if (!(await uiConfirm('Approve and sign off this run? This cannot be undone.', { okLabel: 'Sign off' }))) return;
    setSignOffSaving(true); setSignOffMsg('');
    try {
      await apiFetch(`/production/runs/${runId}/sign-off-approve`, { method: 'POST', body: { approval_id: approvalId } });
      setSignOffMsg('✓ Run signed off successfully.');
      onChanged(); refetchSignOffStatus();
    } catch (err) { setSignOffMsg(`Error: ${err.message}`); }
    finally { setSignOffSaving(false); }
  }
  async function handleSignOff() {
    if (!(await uiConfirm('Sign off this run? This cannot be undone.', { okLabel: 'Sign off' }))) return;
    setSignOffSaving(true); setSignOffMsg('');
    try {
      await apiFetch(`/production/runs/${runId}/signoff-direct`, { method: 'POST' });
      setSignOffMsg('✓ Run signed off successfully.');
      onChanged(); refetchSignOffStatus();
    } catch (err) { setSignOffMsg(`Error: ${err.message}`); }
    finally { setSignOffSaving(false); }
  }
  return (
    <div>
        <>
          <SectionHeader title="Sign-off" />

          {run.status === 'signed_off' && (
            <div style={{ padding: 16, background: '#f0fdf4', borderRadius: 'var(--radius-lg)', border: '1px solid #bbf7d0', fontSize: 14, color: '#166534' }}>
              ✅ This run was signed off on {run.signed_off_at ? new Date(run.signed_off_at).toLocaleString() : '—'}
              {signOffApproval?.requester && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#166534' }}>
                  Requested by <strong>{signOffApproval.requester.name}</strong>
                  {signOffApproval.approver && <> · Approved by <strong>{signOffApproval.approver.name}</strong></>}
                </div>
              )}
            </div>
          )}

          {run.status === 'completed' && (
            <div style={{ padding: 20, background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
              {/* Balance gate notice */}
              {massBalance && !massBalance.is_balanced && (
                <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: 'var(--radius)', border: '1px solid #fecaca', marginBottom: 16, fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
                  ⚠️ Mass balance is unbalanced (variance {Number(massBalance.variance_pct).toFixed(3)}%). Resolve before sign-off.
                </div>
              )}

              {/* No approval yet */}
              {!signOffApproval && (
                <>
                  <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text)' }}>
                    {isOwner
                      ? 'As the owner account, you can sign this run off yourself.'
                      : 'Two signatures are required to sign off this run. Request sign-off below — a second authorised user must then approve.'}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    {isOwner ? (
                      <>
                        <Btn onClick={handleSignOff} disabled={signOffSaving}>
                          {signOffSaving ? 'Signing off…' : '✓ Sign off'}
                        </Btn>
                        <button onClick={handleRequestSignOff} disabled={signOffSaving}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', textDecoration: 'underline', padding: 0 }}>
                          Ask someone else to approve instead
                        </button>
                      </>
                    ) : (
                      <Btn onClick={handleRequestSignOff} disabled={signOffSaving}>
                        {signOffSaving ? 'Requesting…' : '✎ Request Sign-off'}
                      </Btn>
                    )}
                  </div>
                </>
              )}

              {/* Pending approval */}
              {signOffApproval && signOffApproval.status === 'pending' && (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fef9c3', border: '2px solid #ca8a04', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>✎</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>Signature 1 of 2 — request submitted</div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        Requested by <strong style={{ color: 'var(--text)' }}>{signOffApproval.requester?.name ?? '—'}</strong>{' '}
                        at {new Date(signOffApproval.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--surface-2)', border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0, color: 'var(--text-muted)' }}>2</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-muted)', marginBottom: 2 }}>Signature 2 of 2 — awaiting second approver</div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>A different authorised user must approve to complete sign-off.</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    {isOwner && signOffApproval.requested_by === (meData?.data ?? meData)?.id ? (
                      <Btn onClick={handleSignOff} disabled={signOffSaving}>
                        {signOffSaving ? 'Signing off…' : '✓ Sign off now'}
                      </Btn>
                    ) : (
                      <Btn onClick={() => handleApproveSignOff(signOffApproval.id)} disabled={signOffSaving}>
                        {signOffSaving ? 'Approving…' : '✓ Approve & Sign Off'}
                      </Btn>
                    )}
                    <button onClick={handleRequestSignOff} disabled={signOffSaving}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', textDecoration: 'underline', padding: 0 }}>
                      Re-request (cancel and restart)
                    </button>
                  </div>
                </>
              )}

              {signOffMsg && (
                <p style={{ margin: '16px 0 0', fontSize: 13, fontWeight: 600, color: signOffMsg.startsWith('Error') ? '#dc2626' : 'var(--pld-green)' }}>{signOffMsg}</p>
              )}
            </div>
          )}
        </>
    </div>
  );
}

// ── Run screen (rebuilt 15 Oct 2026) ─────────────────────────────────────────
// One run = a row of stages. Each stage shows what went in and what came
// out. Everything that goes in comes off stock from a location you choose;
// a stage can't be finished with anything still to take; finishing carries
// its product into the next stage. See productionFlowService.js.

const UNIT_SHORT = { litres: 'L', litre: 'L', l: 'L' };
const unitOf = (u) => UNIT_SHORT[String(u || '').toLowerCase()] || u || '';
const fmtN = (v, dp = 2) => (v == null ? '—' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const fmtQ = (v) => Number(v).toLocaleString('en-GB', { maximumFractionDigits: 3 });
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');
const fmtWhen = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const C = {
  ok: '#2f6b2a', okBg: '#e7f1e2', warn: '#7a4a00', warnBg: '#fff4d6', warnLine: '#e0b860',
  bad: '#9b2c1a', badBg: '#fde8e4', grey: '#eceee6', line: 'var(--border)',
};

function Pill({ tone = 'grey', children, style }) {
  const t = {
    ok: { background: C.okBg, color: C.ok }, warn: { background: C.warnBg, color: C.warn },
    bad: { background: C.badBg, color: C.bad }, green: { background: '#eef1e8', color: 'var(--pld-green)' },
    grey: { background: 'var(--surface-2)', color: 'var(--text-muted)' },
  }[tone];
  return <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', ...t, ...style }}>{children}</span>;
}

function Dot({ kind }) {
  const colour = { taken: C.ok, carried: 'var(--pld-green)', planned: '#c98a00', product: 'var(--pld-green)', byproduct: 'var(--pld-green)', waste: '#b45309' }[kind] || '#c4c9bd';
  const hollow = kind === 'planned' || kind === 'upcoming' || kind === 'logged' || kind === 'coming';
  return <span aria-hidden="true" style={{ flex: 'none', width: 12, height: 12, borderRadius: 999, border: `2px solid ${colour}`, background: hollow ? 'transparent' : colour }} />;
}

function SummaryCard({ label, value, unit, note, tone }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: tone === 'ok' ? C.ok : tone === 'bad' ? C.bad : 'var(--text)' }}>
        {value} {unit && <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-muted)' }}>{unit}</span>}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{note}</div>
    </div>
  );
}

function LineRow({ mark, name, sub, qty, alcohol, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
      {mark}
      <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{name}</div>
        {sub && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{sub}</div>}
      </div>
      <div style={{ width: 110, textAlign: 'right', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{qty}</div>
      <div style={{ width: 130, textAlign: 'right', fontSize: 13 }}>{alcohol}</div>
      <div style={{ flex: '0 1 260px', display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>{actions}</div>
    </div>
  );
}

function Alcohol({ line }) {
  if (!line.alcoholic) return <span style={{ color: 'var(--text-muted)' }}>No alcohol</span>;
  if (line.kind === 'planned') return <span style={{ color: 'var(--text-muted)' }}>Strength from the batch</span>;
  if (line.kind === 'coming') return <span style={{ color: 'var(--text-muted)' }}>As measured</span>;
  return <><span style={{ fontWeight: 600, color: 'var(--text)' }}>{fmtN(line.lpa)} LPA</span>{line.abv != null && <div style={{ color: 'var(--text-muted)' }}>@ {fmtN(line.abv, 2)}%</div>}</>;
}

const smallBtn = { height: 34, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
const primarySmall = { ...smallBtn, border: 'none', background: 'var(--pld-green)', color: '#fff' };

// Ingredient: add one, take a planned one off stock, or change one.
function IngredientModal({ mode, line, stage, stockItems, locations, onClose, onSave, onRemove, busy }) {
  const [item, setItem] = useState(line ? { id: line.stock_item_id, name: line.name, unit: line.unit, is_alcoholic: line.alcoholic, unlimited_stock: line.unlimited } : null);
  const [search, setSearch] = useState('');
  const [qty, setQty] = useState(line ? String(line.quantity) : '');
  const [loc, setLoc] = useState(line?.location_id || line?.suggested?.location_id || '');
  const [abv, setAbv] = useState('');
  // Where it's held — Blake picks the place with enough; the rest are one tap away.
  const [places, setPlaces] = useState(line?.places || []);
  const [pickedByHand, setPickedByHand] = useState(!!line?.location_id);
  useEffect(() => {
    if (mode !== 'add' || !item?.id || item.unlimited_stock) return;
    let live = true;
    const t = setTimeout(async () => {
      try {
        const r = await apiFetch(`/production/runs/${stage.run_id}/flow/where?item=${item.id}&qty=${Number(qty) || 0}`);
        if (!live) return;
        const list = r.places || [];
        setPlaces(list);
        if (!pickedByHand) setLoc(list.find((p) => p.enough)?.location_id || '');
      } catch { /* leave as is */ }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [item?.id, qty]); // eslint-disable-line react-hooks/exhaustive-deps
  const alcoholic = !!(item && (item.is_alcoholic || Number(item.abv) > 0));
  const unlimited = !!item?.unlimited_stock;
  const ready = item && Number(qty) > 0 && (unlimited || loc);
  const title = mode === 'add' ? `Add an ingredient to ${stage.name}` : mode === 'take' ? `Take ${line.name} off stock` : `Change ${line.name}`;
  return (
    <Modal title={title} onClose={onClose} width={520}>
      <form onSubmit={(e) => { e.preventDefault(); if (ready) onSave({ stock_item_id: item.id, quantity: Number(qty), location_id: loc || null, abv: abv ? Number(abv) : undefined }); }}
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {mode === 'add' && (
          <StockItemPicker label="Ingredient *" items={stockItems} value={item?.id} search={search} onSearchChange={setSearch}
            onSelect={(it) => { setItem(it); if (it) setSearch(it.name); }} />
        )}
        <Field label={`How much${item ? ` (${unitOf(item.unit)})` : ''} *`}>
          <Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus={mode !== 'add'} />
          {mode === 'take' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>From the recipe — change it to what you actually used.</span>}
        </Field>
        {unlimited ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>{item.name} is unlimited — no location needed.</p>
        ) : (
          <Field label="Take it from *">
            {places.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 }}>
                {places.map((pl) => {
                  const on = loc === pl.location_id;
                  return (
                    <button key={pl.location_id} type="button" onClick={() => { setLoc(pl.location_id); setPickedByHand(true); }} aria-pressed={on}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, textAlign: 'left',
                        border: on ? '2px solid var(--pld-green)' : '1px solid var(--border)', background: on ? '#eef1e8' : 'var(--surface)', color: 'var(--text)' }}>
                      <span style={{ fontWeight: on ? 700 : 500 }}>{pl.label}</span>
                      <span style={{ fontSize: 13, color: pl.enough || !(Number(qty) > 0) ? 'var(--text-muted)' : C.warn }}>{fmtQ(pl.quantity)} {unitOf(item?.unit)} there{!pl.enough && Number(qty) > 0 ? ' — not enough' : ''}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {item && places.length === 0 && <span style={{ fontSize: 12, color: C.warn }}>Blake has no {item.name} in stock anywhere — choose where it is.</span>}
            <LocationSelect locations={locations} value={places.some((pl) => pl.location_id === loc) ? '' : loc} onChange={(e) => { setLoc(e.target.value); setPickedByHand(true); }}
              placeholder={places.length ? '— Somewhere else —' : '— Choose the location —'} />
          </Field>
        )}
        {alcoholic && (
          <Field label="Strength (ABV %)">
            <Input type="number" min="0" max="100" step="any" value={abv} onChange={(e) => setAbv(e.target.value)} placeholder="Leave blank to use the batch's strength" />
          </Field>
        )}
        {item && !alcoholic && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>{item.name} has no alcohol — nothing to count for duty.</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {onRemove && <Btn type="button" variant="danger" onClick={onRemove} disabled={busy} style={{ marginRight: 'auto' }}>Remove</Btn>}
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={!ready || busy}>{busy ? 'Saving…' : mode === 'edit' && line?.kind === 'taken' ? 'Save change' : mode === 'edit' ? 'Save' : 'Take off stock'}</Btn>
        </div>
        {mode === 'edit' && line?.kind === 'taken' && <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>Saving gives the old amount back to stock and takes the new one.</p>}
      </form>
    </Modal>
  );
}

// What came out: the product, a by-product kept in stock, or waste (lees).
function OutputModal({ out, stage, stockItems, locations, onClose, onSave, onRemove, busy }) {
  const def = stage.default_output_item;
  const [kind, setKind] = useState(out?.kind || 'product');
  const [item, setItem] = useState(out?.stock_item_id ? { id: out.stock_item_id, name: out.name } : (def || null));
  const [search, setSearch] = useState(out?.stock_item_id ? out.name : def?.name || '');
  const [label, setLabel] = useState(out?.label || 'Lees');
  const [qty, setQty] = useState(out ? String(out.quantity) : '');
  const [abv, setAbv] = useState(out?.abv != null ? String(out.abv) : '');
  const [loc, setLoc] = useState(out?.location_id || stage.default_output_location_id || '');
  const isWaste = kind === 'waste';
  const ready = Number(qty) > 0 && (isWaste ? label.trim() : item && loc);
  const KINDS = [['product', 'The product'], ['byproduct', 'By-product (kept)'], ['waste', 'Waste (e.g. lees)']];
  return (
    <Modal title={out ? 'Change what came out' : `What came out of ${stage.name}`} onClose={onClose} width={540}>
      <form onSubmit={(e) => { e.preventDefault(); if (ready) onSave({ kind, stock_item_id: isWaste ? null : item.id, label: isWaste ? label.trim() : null, quantity: Number(qty), abv: abv === '' ? null : Number(abv), location_id: isWaste ? null : loc }); }}
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div role="radiogroup" aria-label="What it is" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {KINDS.map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)}
              style={{ ...smallBtn, height: 38, ...(kind === k ? { background: 'var(--pld-green)', color: '#fff', borderColor: 'var(--pld-green)' } : {}) }}>{l}</button>
          ))}
        </div>
        {isWaste ? (
          <Field label="What is it? *"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Lees" /></Field>
        ) : (
          <StockItemPicker label="Goes into stock as *" items={stockItems} value={item?.id} search={search} onSearchChange={setSearch}
            onSelect={(it) => { setItem(it); if (it) setSearch(it.name); }} />
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="How much (L) *"><Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
          <Field label="Strength (ABV %)"><Input type="number" min="0" max="100" step="any" value={abv} onChange={(e) => setAbv(e.target.value)} placeholder="As measured" /></Field>
        </div>
        {!isWaste && (
          <Field label="Where is it? *">
            <LocationSelect locations={locations} value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="— The tank or location it's in —" />
          </Field>
        )}
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
          {isWaste ? 'Waste is recorded with its alcohol as a production loss — it doesn\'t go into stock.'
            : kind === 'byproduct' ? 'A by-product goes into stock as its own batch and stays there.'
              : stage.is_last ? 'The finished product goes into stock as a new batch.'
                : `Goes into stock as a batch, then across into ${stage.next_name} when you finish this stage.`}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {onRemove && <Btn type="button" variant="danger" onClick={onRemove} disabled={busy} style={{ marginRight: 'auto' }}>Remove</Btn>}
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={!ready || busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} style={{ ...smallBtn, height: 44, padding: '0 16px', fontSize: 14 }}>More ▾</button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{ position: 'absolute', right: 0, top: 48, zIndex: 41, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', minWidth: 240, padding: 6 }}>
            {shown.map(([label, fn, danger]) => (
              <button key={label} type="button" onClick={() => { setOpen(false); fn(); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', background: 'none', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer', color: danger ? '#b91c1c' : 'var(--text)', fontFamily: 'inherit' }}>{label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RunView({ runId, onBack, onStatusChange }) {
  const [view, setView] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [selId, setSelId] = useState(null);
  const [tab, setTab] = useState('run');
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [sampleLabels, setSampleLabels] = useState(false);
  const [caseLabels, setCaseLabels] = useState(false);
  const { data: stockData } = useApi('/stock');
  const { data: locationsData } = useApi('/warehouse/locations');
  const stockItems = (stockData?.stock_items ?? stockData ?? []).filter((s) => s.is_active !== false);
  const locations = locationsData?.locations ?? locationsData ?? [];

  async function load() {
    try { setView(await apiFetch(`/production/runs/${runId}/flow`)); setLoadErr(''); } catch (e) { setLoadErr(e.message); }
  }
  useEffect(() => { load(); }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(path, method = 'POST', body) {
    setBusy(true);
    try {
      await apiFetch(`/production/runs/${runId}${path}`, { method, body });
      await load(); onStatusChange?.();
      return true;
    } catch (e) { await uiAlert(e.message); return false; } finally { setBusy(false); }
  }
  const save = async (path, method, body) => { if (await act(path, method, body)) setModal(null); };

  if (loadErr && !view) return <div style={{ padding: 40, color: '#dc2626' }}>{loadErr}</div>;
  if (!view) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading…</div>;

  const { run, stages, totals } = view;
  const sel = stages.find((s) => s.id === selId) || stages.find((s) => s.id === view.active_stage_id) || stages[stages.length - 1];
  const activeIdx = stages.findIndex((s) => s.id === view.active_stage_id);
  const editable = run.editable;
  const isActive = sel.state === 'active' && editable;
  const statusPill = {
    planned: ['Planned', 'grey'], in_progress: [`In progress · stage ${activeIdx + 1} of ${stages.length}`, 'warn'], paused: ['Paused', 'warn'],
    completed: ['Complete — ready to sign off', 'ok'], signed_off: ['Signed off', 'ok'], cancelled: ['Cancelled', 'bad'],
  }[run.status] || [run.status, 'grey'];

  const products = sel.outputs.filter((o) => o.kind === 'product');
  let gate; let gateTone = 'muted';
  if (sel.state === 'done') { gate = `Finished ${fmtDay(sel.completed_at)}.${sel.next_name ? ` What came out went across into ${sel.next_name}.` : ''}`; gateTone = 'ok'; }
  else if (sel.state === 'upcoming') { gate = `Starts once ${stages[stages.indexOf(sel) - 1]?.name || 'the stage before'} is finished.`; }
  else if (sel.planned_count) { gate = `${sel.planned_count === 1 ? 'One ingredient is' : `${sel.planned_count} ingredients are`} still to take off stock — take ${sel.planned_count === 1 ? 'it' : 'them'} (or remove ${sel.planned_count === 1 ? 'it' : 'them'}) before finishing.`; gateTone = 'warn'; }
  else if (!products.length) { gate = 'Everything has come off stock. Record what came out to finish.'; gateTone = 'warn'; }
  else { gate = sel.is_last ? 'Ready. Finishing completes the run and leaves the product in stock.' : `Ready. Finishing carries ${products.map((p) => p.name).join(', ')} into ${sel.next_name}.`; gateTone = 'ok'; }
  const canFinish = isActive && !sel.planned_count && products.length > 0;

  const b = sel.balance;
  const balText = b.loss_lpa == null ? (sel.state === 'upcoming' ? 'Not started' : 'Waiting for what came out')
    : b.loss_lpa >= 0 ? `${fmtN(b.loss_lpa)} LPA loss${b.loss_pct != null ? ` · ${fmtN(b.loss_pct, 1)}%` : ''}`
      : `${fmtN(-b.loss_lpa)} LPA more out than in — check the strength reading`;
  const balTone = b.loss_lpa == null ? 'grey' : b.loss_lpa < 0 ? 'bad' : 'ok';

  const stageName = async (current) => {
    const name = await uiPrompt('Stage name', current || '', { okLabel: 'Save' });
    return name && name.trim() ? name.trim() : null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 48, fontVariantNumeric: 'tabular-nums' }}>
      <button onClick={onBack} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Production runs</button>

      {/* Title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{run.run_ref}</div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--text)' }}>{run.recipe_name || run.run_ref}</h1>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 14, color: 'var(--text-muted)' }}>
            <Pill tone={statusPill[1]}>{statusPill[0]}</Pill>
            {run.actual_start_at && <span>Started {fmtDay(run.actual_start_at)}</span>}
            {run.client_name && <span>· {run.client_name}</span>}
            {!run.operates_in_bond && <span>· Not in bond</span>}
            {run.bottle_batch_no && <span>· Bottle batch <strong style={{ color: 'var(--text)' }}>{run.bottle_batch_no}</strong></span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setCaseLabels(true)} style={{ ...smallBtn, height: 44, padding: '0 16px', fontSize: 14 }}>Case labels</button>
          <button type="button" onClick={() => setSampleLabels(true)} style={{ ...smallBtn, height: 44, padding: '0 16px', fontSize: 14 }}>Sample label</button>
          <MoreMenu items={[
            !run.bottle_batch_no && ['Give it a bottle batch number', () => act('/bottle-batch', 'POST', {})],
            run.bottle_batch_no && ['Change the bottle batch number', async () => {
              const n = await uiPrompt('Bottle batch number (printed on the bottles). Leave empty to remove it.', String(run.bottle_batch_no), { okLabel: 'Save' });
              if (n !== null && n !== undefined) act('/bottle-batch', 'POST', { number: String(n).trim() === '' ? null : String(n).trim() });
            }],
            editable && ['Add a stage', async () => { const n = await stageName(''); if (n) act('/flow/stages', 'POST', { name: n }); }],
            editable && [`Rename ${sel.name}`, async () => { const n = await stageName(sel.name); if (n) act(`/flow/stages/${sel.id}`, 'PATCH', { name: n }); }],
            editable && stages.length > 1 && sel.state !== 'done' && [`Remove ${sel.name}`, async () => {
              if (await uiConfirm(`Remove the stage ${sel.name}? Only possible while nothing has gone in or come out.`, { okLabel: 'Remove', danger: true })) act(`/flow/stages/${sel.id}`, 'DELETE');
            }, true],
            run.status === 'cancelled' && ['Reopen this run', async () => { if (await act('/reopen')) setSelId(null); }],
            editable && ['Cancel run and give everything back', async () => {
              const reason = await uiPrompt('Cancel this run? Everything taken off stock goes back, and everything it made comes back off stock. Reason (optional):', '', { okLabel: 'Cancel the run' });
              if (reason !== null) act('/cancel-undo', 'POST', { reason });
            }, true],
          ]} />
        </div>
      </div>

      {/* Spirit summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <SummaryCard label="SPIRIT TAKEN FROM STOCK" value={fmtN(totals.drawn_lpa)} unit="LPA" note="Everything alcoholic that went in" />
        <SummaryCard label="SPIRIT IN THE LIQUID NOW" value={totals.liquid_lpa == null ? '—' : fmtN(totals.liquid_lpa)} unit={totals.liquid_lpa == null ? '' : 'LPA'}
          note={totals.liquid_stage ? `Measured at the end of ${totals.liquid_stage}` : 'Nothing measured yet'} />
        <SummaryCard label="LOSS SO FAR" value={totals.loss_lpa == null ? '—' : fmtN(totals.loss_lpa)} unit={totals.loss_lpa == null ? '' : `LPA${totals.loss_pct != null ? ` · ${fmtN(totals.loss_pct, 1)}%` : ''}`}
          tone={totals.loss_lpa == null ? null : totals.loss_lpa < 0 ? 'bad' : 'ok'}
          note={totals.loss_lpa == null ? 'Shown once something has come out' : totals.loss_lpa < 0 ? 'More out than in — check a strength reading' : 'Across the stages measured'} />
      </div>

      {/* Stage rail */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${stages.length > 4 ? 150 : 180}px, 1fr))`, gap: 10 }}>
        {stages.map((st) => {
          const on = st.id === sel.id;
          const done = st.state === 'done'; const active = st.state === 'active';
          const sub = done ? `Done${st.completed_at ? ` · ${fmtDay(st.completed_at)}` : ''}`
            : active ? (run.status === 'cancelled' ? 'Cancelled' : st.planned_count ? `${st.planned_count} to take off stock` : 'In progress') : 'Up next';
          return (
            <button key={st.id} type="button" onClick={() => setSelId(st.id)} aria-pressed={on}
              style={{ textAlign: 'left', cursor: 'pointer', padding: '14px 16px', borderRadius: 14, minHeight: 76, fontFamily: 'inherit', color: 'var(--text)',
                background: on ? 'var(--surface)' : 'var(--surface-2)', border: on ? '2px solid var(--pld-green)' : '1px solid var(--border)',
                boxShadow: on ? '0 6px 18px rgba(34,57,31,.10)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 28, height: 28, flex: 'none', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                  ...(done ? { background: C.ok, color: '#fff' } : active ? { background: 'var(--pld-yellow)', color: 'var(--pld-green)' } : { background: C.grey, color: 'var(--text-muted)' }) }}>
                  {done ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg> : st.n}
                </span>
                <span style={{ fontSize: 16, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.name}</span>
              </div>
              <div style={{ fontSize: 13, color: active && st.planned_count ? C.warn : 'var(--text-muted)', marginTop: 6 }}>{sub}</div>
            </button>
          );
        })}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)' }}>
        {[['run', 'Stages'], ['qc', 'QC'], ['duty', 'Duty (W1)']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} style={{ padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit',
            fontWeight: tab === k ? 700 : 500, color: tab === k ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: tab === k ? '2px solid var(--pld-green)' : '2px solid transparent', marginBottom: -1 }}>{l}</button>
        ))}
      </div>

      {tab === 'qc' && <QcPanel runId={run.id} view={view} onChanged={load} />}
      {tab === 'duty' && <W1Panel run={run} />}

      {tab === 'run' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
          {/* Stage panel */}
          <div style={{ flex: '999 1 620px', minWidth: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ padding: '20px 22px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', color: 'var(--text-muted)' }}>STAGE {sel.n} OF {stages.length}</div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{sel.name}</h2>
                {sel.notes && <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{sel.notes}</div>}
              </div>
              <Pill tone={run.status === 'cancelled' ? 'bad' : sel.state === 'done' ? 'ok' : sel.state === 'active' ? 'warn' : 'grey'}>
                {run.status === 'cancelled' ? 'Run cancelled' : sel.state === 'done' ? `Done${sel.completed_at ? ` · ${fmtDay(sel.completed_at)}` : ''}` : sel.state === 'active' ? `In progress${sel.started_at ? ` · since ${fmtDay(sel.started_at)}` : ''}` : 'Not started'}
              </Pill>
            </div>

            {/* Went in */}
            <div style={{ padding: '18px 22px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Went in</h3>
              {isActive && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {sel.planned_count > 1 && <button type="button" disabled={busy} onClick={() => act(`/flow/stages/${sel.id}/take-all`)} style={primarySmall}>Take all {sel.planned_count} off stock</button>}
                  <button type="button" onClick={() => setModal({ type: 'add' })} style={{ ...smallBtn, color: 'var(--pld-green)' }}>+ Add ingredient</button>
                </div>
              )}
            </div>
            <div style={{ padding: '0 22px' }}>
              {sel.inputs.length === 0 && <div style={{ padding: '12px 0', borderTop: '1px solid var(--border)', fontSize: 14, color: 'var(--text-muted)' }}>Nothing yet.</div>}
              {sel.inputs.map((ln) => (
                <LineRow key={ln.id} mark={<Dot kind={ln.kind} />} name={ln.name}
                  sub={ln.kind === 'taken' ? (ln.location ? `From ${ln.location}` : ln.unlimited ? 'Unlimited supply' : 'Taken off stock')
                    : ln.kind === 'coming' ? `Comes across from ${ln.from_stage} when it's finished`
                    : ln.kind === 'carried' ? `Came across from ${ln.from_stage || 'the stage before'}`
                      : ln.kind === 'planned' ? (ln.unlimited ? 'Planned · unlimited, no location needed'
                        : ln.suggested ? `Planned · from ${ln.suggested.label} (${fmtQ(ln.suggested.quantity)} ${unitOf(ln.unit)} there)`
                          : ln.places.length ? 'Planned · not enough in one place — choose where' : 'Planned · none in stock')
                        : 'Not a stock item'}
                  qty={`${fmtQ(ln.quantity)} ${unitOf(ln.unit)}`}
                  alcohol={<Alcohol line={ln} />}
                  actions={<>
                    {ln.kind === 'taken' && <><Pill tone="ok">Off stock</Pill>{isActive && <button type="button" style={smallBtn} onClick={() => setModal({ type: 'editIn', line: ln })}>Edit</button>}</>}
                    {ln.kind === 'carried' && <Pill tone="green">From {ln.from_stage || 'the stage before'}</Pill>}
                    {ln.kind === 'coming' && <Pill tone="grey">From {ln.from_stage}</Pill>}
                    {ln.kind === 'planned' && isActive && <>
                      <button type="button" style={smallBtn} onClick={() => setModal({ type: 'take', line: ln })}>Change</button>
                      {ln.suggested || ln.unlimited
                        ? <button type="button" style={primarySmall} disabled={busy} onClick={() => act(`/flow/inputs/${ln.id}/take`, 'POST', {})}>Take off stock</button>
                        : <button type="button" style={{ ...primarySmall, background: C.warn }} onClick={() => setModal({ type: 'take', line: ln })}>Choose where</button>}
                    </>}
                  </>} />
              ))}
            </div>

            {/* Came out */}
            <div style={{ padding: '20px 22px 6px', borderTop: '1px solid var(--border)', marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Came out</h3>
              {isActive && sel.outputs.length > 0 && <button type="button" onClick={() => setModal({ type: 'output' })} style={{ ...smallBtn, color: 'var(--pld-green)' }}>+ Record more</button>}
            </div>
            <div style={{ padding: '0 22px' }}>
              {sel.outputs.map((o) => (
                <LineRow key={o.id} mark={<Dot kind={o.kind} />} name={o.name}
                  sub={o.kind === 'waste' ? 'Waste — counted as a production loss' : [o.location && `In ${o.location}`, o.batch_ref && `batch ${o.batch_ref}`].filter(Boolean).join(' · ')}
                  qty={`${fmtQ(o.quantity)} ${unitOf(o.unit)}`}
                  alcohol={o.alcoholic ? <><span style={{ fontWeight: 600, color: 'var(--text)' }}>{fmtN(o.lpa)} LPA</span>{o.abv != null && <div style={{ color: 'var(--text-muted)' }}>@ {fmtN(o.abv, 2)}%</div>}</> : <span style={{ color: 'var(--text-muted)' }}>No alcohol</span>}
                  actions={<>
                    <Pill tone={o.kind === 'waste' ? 'warn' : 'green'}>{o.kind === 'waste' ? 'Waste' : o.carried_on ? `Into ${sel.next_name}` : o.kind === 'byproduct' ? 'By-product · in stock' : 'In stock'}</Pill>
                    {isActive && <button type="button" style={smallBtn} onClick={() => setModal({ type: 'output', out: o })}>Edit</button>}
                  </>} />
              ))}
              {sel.outputs.length === 0 && (
                <div style={{ padding: 16, margin: '4px 0 6px', border: '1.5px dashed var(--border)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                    {sel.state === 'upcoming' ? 'Nothing yet.' : `Nothing recorded yet. Measure the volume and strength${sel.default_output_item ? ` of the ${sel.default_output_item.name}` : ''} when it's ready.`}
                  </div>
                  {isActive && <button type="button" onClick={() => setModal({ type: 'output' })} style={{ ...smallBtn, height: 40, border: '1px solid var(--pld-green)', color: 'var(--pld-green)' }}>Record what came out</button>}
                </div>
              )}
            </div>

            {/* Spirit this stage */}
            <div style={{ margin: '16px 22px 0', padding: '14px 16px', borderRadius: 12, background: 'var(--surface-2)', display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', color: 'var(--text-muted)' }}>SPIRIT THIS STAGE</div>
              <div style={{ fontSize: 15, color: 'var(--text)' }}><strong>{fmtN(b.lpa_in)}</strong> LPA in</div>
              <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>→</span>
              <div style={{ fontSize: 15, color: 'var(--text)' }}><strong>{b.lpa_out == null ? '—' : fmtN(b.lpa_out)}</strong> LPA out</div>
              <Pill tone={balTone} style={{ marginLeft: 'auto' }}>{balText}</Pill>
            </div>

            {/* Next step */}
            <div style={{ padding: '18px 22px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14, fontWeight: 500, flex: '1 1 260px', color: gateTone === 'ok' ? C.ok : gateTone === 'warn' ? C.warn : 'var(--text-muted)' }}>
                {run.status === 'cancelled' ? 'This run is cancelled. Reopen it from More to start again.' : run.status === 'signed_off' ? 'This run is signed off.' : gate}
              </div>
              {isActive && (
                <Btn onClick={async () => { if (await act(`/flow/stages/${sel.id}/finish`)) setSelId(null); }} disabled={!canFinish || busy} style={{ height: 44 }}>
                  {sel.is_last ? 'Finish the run' : `Finish ${sel.name}`}
                </Btn>
              )}
              {sel.state === 'done' && sel.can_reopen && editable && (
                <button type="button" style={{ ...smallBtn, height: 40 }} disabled={busy} onClick={async () => {
                  if (await uiConfirm(`Reopen ${sel.name}?${sel.next_name ? ` What it carried into ${sel.next_name} goes back, so you can change it.` : ''}`, { okLabel: 'Reopen' })) act(`/flow/stages/${sel.id}/reopen`);
                }}>Reopen stage</button>
              )}
            </div>
          </div>

          {/* Record */}
          <div style={{ flex: '1 1 300px', minWidth: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Record</h3>
            {view.log.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>Everything done on this run shows here.</div>}
            <div style={{ maxHeight: 620, overflowY: 'auto' }}>
              {view.log.map((l) => (
                <div key={l.id} style={{ display: 'flex', gap: 12, padding: '9px 0', borderTop: '1px solid var(--border)' }}>
                  <div style={{ width: 64, flex: 'none', fontSize: 12, color: 'var(--text-muted)', paddingTop: 2 }}>{fmtWhen(l.created_at)}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--text)' }}>{l.text}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{[l.user_name, stages.find((s) => s.id === l.stage_id)?.name].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {(run.status === 'completed' || run.status === 'signed_off') && tab === 'run' && (
        <SignOffPanel run={run} onChanged={() => { load(); onStatusChange?.(); }} />
      )}

      {modal?.type === 'add' && (
        <IngredientModal mode="add" stage={{ ...sel, run_id: run.id }} stockItems={stockItems} locations={locations} busy={busy}
          onClose={() => setModal(null)} onSave={(body) => save(`/flow/stages/${sel.id}/inputs`, 'POST', body)} />
      )}
      {modal?.type === 'take' && (
        <IngredientModal mode="take" line={modal.line} stage={{ ...sel, run_id: run.id }} stockItems={stockItems} locations={locations} busy={busy}
          onClose={() => setModal(null)} onSave={(body) => save(`/flow/inputs/${modal.line.id}/take`, 'POST', body)}
          onRemove={async () => {
            if (await uiConfirm(`Remove the planned ${modal.line.name}? Nothing has come off stock for it.`, { okLabel: 'Remove', danger: true })) save(`/flow/inputs/${modal.line.id}`, 'DELETE');
          }} />
      )}
      {modal?.type === 'editIn' && (
        <IngredientModal mode="edit" line={modal.line} stage={{ ...sel, run_id: run.id }} stockItems={stockItems} locations={locations} busy={busy}
          onClose={() => setModal(null)} onSave={(body) => save(`/flow/inputs/${modal.line.id}`, 'PATCH', body)}
          onRemove={async () => {
            if (await uiConfirm(modal.line.kind === 'taken' ? `Remove ${modal.line.name}? It goes back to stock.` : `Remove the planned ${modal.line.name}?`, { okLabel: 'Remove', danger: true })) save(`/flow/inputs/${modal.line.id}`, 'DELETE');
          }} />
      )}
      {modal?.type === 'output' && (
        <OutputModal out={modal.out} stage={sel} stockItems={stockItems} locations={locations} busy={busy}
          onClose={() => setModal(null)}
          onSave={(body) => (modal.out ? save(`/flow/outputs/${modal.out.id}`, 'PATCH', body) : save(`/flow/stages/${sel.id}/outputs`, 'POST', body))}
          onRemove={modal.out ? async () => {
            if (await uiConfirm(`Remove ${modal.out.name}?${modal.out.kind !== 'waste' ? ' Its batch comes back off stock.' : ''}`, { okLabel: 'Remove', danger: true })) save(`/flow/outputs/${modal.out.id}`, 'DELETE');
          } : null} />
      )}
      {caseLabels && <CaseLabelModal run={run} onClose={() => setCaseLabels(false)} />}
      {sampleLabels && (
        <ProductionSampleLabelModal run={run}
          outputs={stages.flatMap((s) => s.outputs).filter((o) => o.kind === 'product').map((o) => ({ output_type: 'primary', item_name: o.name, abv: o.abv }))}
          onClose={() => setSampleLabels(false)} />
      )}
    </div>
  );
}

// ── Runs list ─────────────────────────────────────────────────────────────────
const STATUS_FILTERS = ['all', 'planned', 'in_progress', 'paused', 'completed', 'signed_off', 'cancelled'];

function RunsList({ onSelectRun }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate]     = useState(false);
  const [find, setFind] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => { const t = setTimeout(() => setTerm(find.trim()), 300); return () => clearTimeout(t); }, [find]);

  const query = `?${new URLSearchParams({ ...(statusFilter === 'all' ? {} : { status: statusFilter }), ...(term ? { q: term } : {}) })}`;
  const { data, loading, error, refetch } = useApi(`/production/runs${query}`);
  const runs = data?.runs ?? [];
  const total = data?.total ?? 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Production</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{total} run{total !== 1 ? 's' : ''} total</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a run, recipe or bottle batch no." aria-label="Find a run" style={{ width: 280 }} />
          <Btn onClick={() => setShowCreate(true)}>+ New Run</Btn>
        </div>
      </div>

      {/* Status filter tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {STATUS_FILTERS.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{
            padding: '7px 14px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: statusFilter === s ? 700 : 400,
            color: statusFilter === s ? 'var(--pld-green)' : 'var(--text-muted)',
            borderBottom: statusFilter === s ? '2px solid var(--pld-green)' : '2px solid transparent',
            marginBottom: -1, textTransform: 'capitalize', whiteSpace: 'nowrap',
          }}>{s.replace('_', ' ')}</button>
        ))}
      </div>

      {/* Table */}
      {loading && <p style={{ color: 'var(--text-muted)', padding: '20px 0' }}>Loading…</p>}
      {error   && <p style={{ color: '#dc2626', padding: '20px 0' }}>Error: {error}</p>}

      {!loading && !error && runs.length === 0 && (
        <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⚗️</div>
          <p style={{ margin: 0, fontSize: 15 }}>No production runs yet.</p>
          <p style={{ margin: '4px 0 16px', fontSize: 13 }}>Create your first run to get started.</p>
          <Btn onClick={() => setShowCreate(true)}>+ New Run</Btn>
        </div>
      )}

      {runs.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                {['Run ref', 'Context', 'Recipe', 'Status', 'Scheduled start', 'Target vol.', ''].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map((run, i) => (
                <tr
                  key={run.id}
                  style={{ borderBottom: i < runs.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', transition: 'background .1s' }}
                  onClick={() => onSelectRun(run.id)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--text)' }}>{run.run_ref}{run.bottle_batch_no && <div style={{ fontFamily: 'inherit', fontWeight: 500, fontSize: 12, color: 'var(--text-muted)' }}>Bottle batch {run.bottle_batch_no}</div>}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 13 }}>{run.business_context === 'CONTRACT' ? 'Contract' : 'Own Brand'}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text)' }}>{run.recipe_name ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td style={{ padding: '12px 16px' }}><StatusBadge status={run.status} /></td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
                    {run.scheduled_start_date ? new Date(run.scheduled_start_date).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
                    {run.target_volume_litres ? `${run.target_volume_litres}L` : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>→</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateRunModal
          onClose={() => setShowCreate(false)}
          onCreated={(run) => { setShowCreate(false); refetch(); if (run?.id) onSelectRun(run.id); }}
        />
      )}
    </div>
  );
}

// ── Page root ─────────────────────────────────────────────────────────────────
export default function Production() {
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [listKey, setListKey] = useState(0); // force list refetch after status change

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 8px' }}>
      {selectedRunId ? (
        <RunView
          key={selectedRunId}
          runId={selectedRunId}
          onBack={() => setSelectedRunId(null)}
          onStatusChange={() => setListKey(k => k + 1)}
        />
      ) : (
        <RunsList key={listKey} onSelectRun={setSelectedRunId} />
      )}
    </div>
  );
}
